import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, Building2, FileText, Link2, Check, Video, ChevronDown, ChevronUp, ThumbsUp, Minus, ThumbsDown, MessageSquare, Send } from 'lucide-react';
import { quotesApi } from '../services/api';
import toast from 'react-hot-toast';
import QuoteAcceptanceForm from './QuoteAcceptanceForm';
import QuotePasswordGate from './QuotePasswordGate';
import { buildTheme, buildPrintTheme, getLayoutId } from '../data/landingPageTemplates';
import { flushSync } from 'react-dom';
import { getLayoutComponent } from './quoteLayouts';
import { serviceLineTotal } from './quoteLayouts/lib';
import { RichText } from './quoteLayouts/SharedBlocks';

const PublicQuoteView = () => {
  const { viewHash } = useParams();
  const [searchParams] = useSearchParams();
  const shouldPrint = searchParams.get('print') === 'true';
  const [quoteData, setQuoteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revoked, setRevoked] = useState(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  // Druckmodus: dunkle Layouts drucken als helle Version (Tinte + Lesbarkeit)
  const [isPrinting, setIsPrinting] = useState(false);

  // ── Session-Tracking (datenschutzfreundlich): zufällige ID NUR im RAM
  // (kein Cookie, kein Storage), keine IP, kein User-Agent. Erfasst werden
  // Zeitstempel, Geräteklasse und Nutzungsereignisse der Angebotsseite.
  const sessionIdRef = React.useRef(
    (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
    `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );
  const sessionEvent = React.useCallback((type, label = '') => {
    try {
      quotesApi.sessionEvent(viewHash, { sessionId: sessionIdRef.current, type, label,
        device: window.innerWidth < 768 ? 'mobile' : 'desktop' }).catch(() => {});
    } catch {}
  }, [viewHash]);

  useEffect(() => {
    if (!viewHash) return;
    sessionEvent('start');
    const hb = setInterval(() => {
      if (document.visibilityState === 'visible') sessionEvent('heartbeat');
    }, 15000);
    // Sektion aufgeklappt → Titel protokollieren
    const onToggle = (e) => {
      const d = e.target;
      if (d?.classList?.contains('qsec-details') && d.open) {
        const title = d.querySelector('h3')?.textContent?.trim() || 'Sektion';
        sessionEvent('section_open', title);
      }
    };
    document.addEventListener('toggle', onToggle, true);
    return () => { clearInterval(hb); document.removeEventListener('toggle', onToggle, true); };
  }, [viewHash, sessionEvent]);
  const [linkCopied, setLinkCopied] = useState(false);
  const [optionalServicesSelected, setOptionalServicesSelected] = useState({});
  const [selectedAlternatives, setSelectedAlternatives] = useState({});
  const [showAcceptanceForm, setShowAcceptanceForm] = useState(false);
  const [quoteAccepted, setQuoteAccepted] = useState(false);
  // Collapsible sections state
  const [collapsedSections, setCollapsedSections] = useState({});
  // Editable quantities
  const [localQuantities, setLocalQuantities] = useState({});
  // Feedback
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  // Customer questions
  const [questionForm, setQuestionForm] = useState({ name: '', email: '', question: '' });
  const [questionSubmitted, setQuestionSubmitted] = useState(false);
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const startTimeRef = useRef(Date.now());
  const hasTrackedView = useRef(false);
  const hasPrinted = useRef(false);
  
  // Helper: toggle section collapse
  const toggleSection = (key) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  const isSectionCollapsed = (key) => collapsedSections[key] === true;

  // Helper: get effective quantity (local override or original)
  const getQty = (service) => localQuantities[service.id] !== undefined ? localQuantities[service.id] : service.quantity;

  // Helper: get effective price with tiers and local qty
  const getEffectiveTotal = (service) => {
    const qty = getQty(service);
    const unitPrice = service.customPrice !== null && service.customPrice !== undefined ? service.customPrice : service.price;
    // Ein im Kalkulator gesetzter Einzelpreis schlaegt die Katalog-Staffel —
    // sonst zeigt das Angebot trotz Speichern weiter den Katalogpreis.
    const hasCustom = service.customPrice !== null && service.customPrice !== undefined;
    if (!hasCustom && service.priceTiers && service.priceTiers.length > 0) {
      const tier = service.priceTiers.find(t => t.upTo === null || qty <= t.upTo) || service.priceTiers[service.priceTiers.length - 1];
      return (tier.pricePerUnit ?? unitPrice) * qty;
    }
    return unitPrice * qty;
  };

  // Submit feedback
  const handleFeedback = async (rating) => {
    setFeedbackRating(rating);
  };
  const submitFeedback = async () => {
    if (!feedbackRating) return;
    setSubmittingFeedback(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/view/${viewHash}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: feedbackRating, comment: feedbackComment })
      });
      setFeedbackSubmitted(true);
      toast.success('Vielen Dank für Ihr Feedback!');
    } catch { toast.error('Fehler beim Senden des Feedbacks'); }
    finally { setSubmittingFeedback(false); }
  };

  // Submit question
  const submitQuestion = async (e) => {
    e.preventDefault();
    if (!questionForm.name || !questionForm.question) { toast.error('Bitte Name und Frage ausfüllen'); return; }
    setSubmittingQuestion(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/view/${viewHash}/question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(questionForm)
      });
      setQuestionSubmitted(true);
      toast.success('Ihre Frage wurde übermittelt!');
    } catch { toast.error('Fehler beim Senden der Frage'); }
    finally { setSubmittingQuestion(false); }
  };
  
  // Check if already unlocked in session
  useEffect(() => {
    const unlocked = sessionStorage.getItem(`quote_access_${viewHash}`);
    if (unlocked === 'granted') {
      setIsUnlocked(true);
    }
  }, [viewHash]);

  useEffect(() => {
    const before = () => {
      try { flushSync(() => setIsPrinting(true)); } catch { setIsPrinting(true); }
      document.querySelectorAll('details').forEach(d => { d.dataset.wasOpen = d.open ? '1' : ''; d.open = true; });
    };
    const after = () => {
      setIsPrinting(false);
      document.querySelectorAll('details').forEach(d => { if (d.dataset.wasOpen === '') d.open = false; delete d.dataset.wasOpen; });
    };
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => { window.removeEventListener('beforeprint', before); window.removeEventListener('afterprint', after); };
  }, []);

  const handleSaveAsPdf = () => {
    sessionEvent('pdf');
    try {
      // Set document title for better PDF filename
      const originalTitle = document.title;
      const quoteNumber = quote?.quoteNumber || 'Angebot';
      const customerName = quote?.customerData?.company || 'Kunde';
      document.title = `${quoteNumber}_${customerName}`;
      
      // Helles Druck-Theme rendern, BEVOR der Dialog den Snapshot zieht
      try { flushSync(() => setIsPrinting(true)); } catch { setIsPrinting(true); }

      // Add printing class to body
      document.body.classList.add('printing');
      
      // Open all details elements (AGB) before printing
      const detailsElements = document.querySelectorAll('details');
      const originalStates = [];
      detailsElements.forEach((details, index) => {
        originalStates[index] = details.open;
        details.open = true;
      });
      
      // Small delay to ensure DOM updates
      setTimeout(() => {
        // Open print dialog
        window.print();
        
        // Restore everything after print dialog closes
        setTimeout(() => {
          setIsPrinting(false);
          document.body.classList.remove('printing');
          document.title = originalTitle;
          detailsElements.forEach((details, index) => {
            details.open = originalStates[index];
          });
        }, 500);
      }, 100);
      
      toast.success('Druckdialog geöffnet - wählen Sie "Als PDF speichern"');
    } catch (error) {
      console.error('PDF error:', error);
      toast.error('PDF-Export fehlgeschlagen');
    }
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    toast.success('Link kopiert!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const toggleOptionalService = (serviceId) => {
    try {
      const svc = (services || []).find(x => x.id === serviceId);
      const willBeOn = !optionalServicesSelected[serviceId];
      sessionEvent(willBeOn ? 'optional_on' : 'optional_off', (svc?.name || serviceId).slice(0, 80));
    } catch {}
    setOptionalServicesSelected(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };
  
  const selectAlternative = (serviceId, alternativeId) => {
    setSelectedAlternatives(prev => ({
      ...prev,
      [serviceId]: alternativeId
    }));
  };

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await quotesApi.getByViewHash(viewHash);
        setQuoteData(response.data);
        
        // Check if already accepted
        if (response.data.quote.acceptance || response.data.quote.acceptedAt) {
          setQuoteAccepted(true);
        }
        
        setLoading(false);

        // Track view (only once)
        if (!hasTrackedView.current) {
          quotesApi.trackView(viewHash, {}).catch(err => console.error('Track view error:', err));
          hasTrackedView.current = true;
        }
      } catch (err) {
        console.error('Error fetching quote:', err);
        // 410 = Zugriff wurde widerrufen (Link deaktiviert), nicht "nicht gefunden"
        if (err?.response?.status === 410) {
          setRevoked(err.response.data?.detail || 'Dieses Angebot ist nicht mehr verfügbar.');
        } else {
          setError('Angebot konnte nicht geladen werden');
        }
        setLoading(false);
      }
    };

    if (viewHash) {
      fetchQuote();
    }

    return () => {
      const duration = (Date.now() - startTimeRef.current) / 1000;
      if (duration > 0 && hasTrackedView.current) {
        quotesApi.trackView(viewHash, { duration }).catch(err => console.error('Track duration error:', err));
      }
    };
  }, [viewHash]);

  // Auto-print when ?print=true parameter is present
  useEffect(() => {
    if (shouldPrint && quoteData && !loading && !hasPrinted.current) {
      hasPrinted.current = true;
      // Small delay to ensure page is fully rendered
      setTimeout(() => {
        handleSaveAsPdf();
      }, 1000);
    }
  }, [shouldPrint, quoteData, loading]);

  const handleElementClick = (elementName) => {
    quotesApi.trackClick(viewHash, {
      element: elementName,
      position: { x: 0, y: 0 },
      metadata: { timestamp: new Date().toISOString() }
    }).catch(err => console.error('Track click error:', err));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Angebot wird geladen...</p>
        </div>
      </div>
    );
  }

  if (revoked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Link2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Angebot nicht mehr verfügbar</h2>
            <p className="text-slate-600">{revoked}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !quoteData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Angebot nicht gefunden</h2>
            <p className="text-slate-600">{error || 'Das angeforderte Angebot existiert nicht.'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { quote, company, services } = quoteData;
  
  // Passwort-Prüfung NACH Loading
  if (!isUnlocked && quote?.passwordProtected) {
    return (
      <QuotePasswordGate
        onUnlock={setIsUnlocked}
        customerCity={quote?.customerData?.city || ''}
        customPassword={quote?.viewPassword}
        viewHash={viewHash}
      />
    );
  }

  const companyColors = {
    einmalig: company?.colorEinmalig || '#22c55e',
    monatlich: company?.colorMonatlich || '#f97316',
    jaehrlich: company?.colorJaehrlich || '#a855f7',
    primary: company?.primaryColor || '#3b82f6',
  };

  // Dark template support
  const isDarkTemplate = company?.landingPageTemplate === 'dark';
  const cardBg = isDarkTemplate ? '#1e293b' : '#ffffff';
  const cardBorder = isDarkTemplate ? '#334155' : '#e2e8f0';
  const textPrimary = isDarkTemplate ? '#f1f5f9' : '#1e293b';
  const textSecondary = isDarkTemplate ? '#94a3b8' : '#64748b';

  // Template font + border-radius
  const { getTemplateById } = require('../data/landingPageTemplates');
  const currentTemplate = getTemplateById(company?.landingPageTemplate || 'modern');
  const templateFont = currentTemplate?.font || "'Inter', system-ui, sans-serif";
  const templateRadius = currentTemplate?.borderRadius || '12px';

  // Group services by type
  const einmaligServices = services?.filter(s => s.type === 'einmalig') || [];
  const monatlichServices = services?.filter(s => s.type === 'monatlich' || s.type === 'variabel') || [];
  const jaehrlichServices = services?.filter(s => s.type === 'jährlich' || s.type === 'jaehrlich') || [];

  // Calculate totals with optional services, alternatives, and discounts
  const calculateTotals = (serviceList, serviceType) => {
    let subtotal = 0;
    let optionalSubtotal = 0;

    serviceList.forEach(service => {
      // Check if there's a selected alternative
      let effectivePrice = service.customPrice !== null && service.customPrice !== undefined ? service.customPrice : service.price;
      
      // Handle new alternative system (hasAlternative)
      // WICHTIG: Auf Landingpage entscheidet der Kunde via selectedAlternatives State
      if (service.hasAlternative && selectedAlternatives[service.id] === 'alternative' && service.alternativePrice) {
        effectivePrice = service.alternativePrice;
      }
      // Handle legacy alternative system
      else if (service.alternatives && service.alternatives.length > 0) {
        const selectedAltId = selectedAlternatives[service.id] || service.selectedAlternativeId;
        if (selectedAltId && selectedAltId !== 'main') {
          const selectedAlt = service.alternatives.find(a => a.id === selectedAltId);
          if (selectedAlt) {
            effectivePrice = selectedAlt.price;
          }
        }
      }
      
      let total;
      // Use local quantity if set (bearbeitbare Mengen feature)
      const qty = localQuantities[service.id] !== undefined ? localQuantities[service.id] : service.quantity;
      // Staffelpreise nur, wenn kein individueller Preis gesetzt wurde
      const hasCustomPrice = service.customPrice !== null && service.customPrice !== undefined;
      if (!hasCustomPrice && service.priceTiers && service.priceTiers.length > 0) {
        const activeTier = service.priceTiers.find(t => t.upTo === null || qty <= t.upTo)
          || service.priceTiers[service.priceTiers.length - 1];
        total = (activeTier.pricePerUnit ?? effectivePrice) * qty;
      } else {
        total = effectivePrice * qty;
      }
      
      // Apply individual service discount (customDiscount)
      if (service.customDiscount?.value > 0) {
        if (service.customDiscount.type === 'percentage') {
          total = total * (1 - service.customDiscount.value / 100);
        } else {
          total = Math.max(0, total - service.customDiscount.value);
        }
      }
      
      if (service.isOptional) {
        if (optionalServicesSelected[service.id]) {
          optionalSubtotal += total;
        }
      } else {
        subtotal += total;
      }
    });

    // Apply global discount based on service type - NOW ALSO ON OPTIONAL POSITIONS
    const totalBeforeDiscount = subtotal + optionalSubtotal;
    let globalDiscount = 0;
    let discountInfo = null; // Store discount type and value for display
    
    if (quote.discount) {
      if (serviceType === 'einmalig' && quote.discount.oneTime?.value > 0) {
        discountInfo = quote.discount.oneTime;
        if (quote.discount.oneTime.type === 'percentage') {
          globalDiscount = totalBeforeDiscount * (quote.discount.oneTime.value / 100);
        } else {
          globalDiscount = Math.min(quote.discount.oneTime.value, totalBeforeDiscount);
        }
      } else if (serviceType === 'monatlich' && quote.discount.monthly?.value > 0) {
        discountInfo = quote.discount.monthly;
        if (quote.discount.monthly.type === 'percentage') {
          globalDiscount = totalBeforeDiscount * (quote.discount.monthly.value / 100);
        } else {
          globalDiscount = Math.min(quote.discount.monthly.value, totalBeforeDiscount);
        }
      } else if (serviceType === 'jaehrlich' && quote.discount.yearly?.value > 0) {
        discountInfo = quote.discount.yearly;
        if (quote.discount.yearly.type === 'percentage') {
          globalDiscount = totalBeforeDiscount * (quote.discount.yearly.value / 100);
        } else {
          globalDiscount = Math.min(quote.discount.yearly.value, totalBeforeDiscount);
        }
      }
    }

    const finalSubtotal = totalBeforeDiscount;
    const discountedTotal = Math.max(0, finalSubtotal - globalDiscount);
    const netto = discountedTotal;
    const mwst = discountedTotal * 0.19;
    const brutto = discountedTotal * 1.19;

    return { subtotal, optionalSubtotal, finalSubtotal, globalDiscount, discountInfo, discountedTotal, netto, mwst, brutto };
  };

  const einmaligTotals = calculateTotals(einmaligServices, 'einmalig');
  const monatlichTotals = calculateTotals(monatlichServices, 'monatlich');
  const jaehrlichTotals = calculateTotals(jaehrlichServices, 'jaehrlich');

  // ── Layout-Verzweigung: neue Layout-Archetypen (Pitch/Premium/Onepager) ──────
  // 'document' fällt durch zum eingebauten (unveränderten) Render unten.
  const layoutId = getLayoutId(company);
  const LayoutComponent = getLayoutComponent(layoutId);
  if (LayoutComponent) {
    const theme = isPrinting ? buildPrintTheme(company) : buildTheme(company);
    const refreshQuote = () => quotesApi.getByViewHash(viewHash).then(r => setQuoteData(r.data)).catch(() => {});
    const lineTotal = (s) => serviceLineTotal(s, { selectedAlternatives, localQuantities });
    const ctx = {
      viewHash, quote, company, services,
      einmaligServices, monatlichServices, jaehrlichServices,
      einmaligTotals, monatlichTotals, jaehrlichTotals,
      selectedAlternatives, selectAlternative, setSelectedAlternatives,
      optionalServicesSelected, toggleOptionalService,
      localQuantities, getQty, setLocalQuantities,
      lineTotal,
      showAcceptanceForm, setShowAcceptanceForm, quoteAccepted, setQuoteAccepted, refreshQuote,
      handleSaveAsPdf, handleCopyLink, linkCopied, handleElementClick,
    };
    return (
      <>
        <style>{`
          /* ── Mobile: greift für alle Signature-Layouts (inline-Styles werden via !important überschrieben) ── */
          @media (max-width: 640px) {
            #printable-quote h1 { font-size: clamp(24px, 8.5vw, 40px) !important; line-height: 1.12 !important; letter-spacing: -0.5px !important; }
            #printable-quote [style*="grid-template-columns"] { grid-template-columns: 1fr !important; }
            #printable-quote [data-layout-pad] { padding-left: 18px !important; padding-right: 18px !important; }
          }
          /* ── Druckversion ──
             Globale print.css versteckt alles via "body * { visibility:hidden }" und zeigt nur
             [data-quote-preview]. Die Signature-Layouts haben das nicht — daher machen wir den
             #printable-quote-Teilbaum hier explizit sichtbar und zwingen ihn in sauberes S/W
             auf Weiß (dunkle Layouts würden sonst Tinte fressen / unleserlich drucken). */
          @media print {
            @page { margin: 14mm; size: A4 portrait; }
            .no-print { display: none !important; }
            #printable-quote, #printable-quote * { visibility: visible !important; }
            #printable-quote { position: absolute !important; left: 0; top: 0; width: 100%; }
            #printable-quote * {
              background: #fff !important; background-image: none !important;
              color: #111 !important; box-shadow: none !important; text-shadow: none !important;
              border-color: #ddd !important;
            }
            #printable-quote img { filter: none !important; }
            details, details > summary, details > summary ~ * { display: block !important; }
          }
        `}</style>
        <div id="printable-quote" style={{ '--card-radius': theme.radius }}>
          <LayoutComponent ctx={ctx} theme={theme} />
        </div>
      </>
    );
  }

  return (
    <>
      {/* Print CSS - uses same data-quote-preview attribute as Step 4 for consistent styling */}
      <style>{`
        @media print {
          /* Page settings */
          @page {
            margin: 15mm;
            size: A4 portrait;
          }
          
          /* Force AGB details to be open when printing */
          details,
          details > summary,
          details > summary ~ * {
            display: block !important;
            visibility: visible !important;
          }
        }
      `}</style>
      
    <div 
      className="min-h-screen py-12 px-4 print:bg-white print:py-0 print:px-0" 
      id="printable-quote"
      style={{ 
        background: company?.landingPageBgColor || '#f1f5f9',
        color: textPrimary,
        fontFamily: templateFont,
        '--card-radius': templateRadius
      }}
    >
      <div className="max-w-4xl mx-auto space-y-6 print:max-w-full print:space-y-2" data-quote-preview>
        
        {/* Action Buttons */}
        <div className="flex justify-end gap-3 no-print">
          <Button
            onClick={handleSaveAsPdf}
            variant="outline"
            className="flex items-center gap-2 bg-white shadow-md hover:shadow-lg"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Als PDF speichern
          </Button>
          <Button
            onClick={handleCopyLink}
            variant="outline"
            className="flex items-center gap-2 bg-white shadow-md hover:shadow-lg"
          >
            {linkCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Link2 className="w-4 h-4" />}
            {linkCopied ? 'Kopiert!' : 'Link kopieren'}
          </Button>
        </div>

        {/* Header Card */}
        <Card className="shadow-xl border-t-4 print-card" style={{ borderTopColor: companyColors.primary }}>
          <CardContent className="pt-8">
            {/* Video Presentation (nur auf Landing Page, nicht im PDF) */}
            {quote.videoUrl && (
              <div className="mb-6 no-print">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <h3 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Video className="w-5 h-5" />
                    Persönliche Video-Präsentation
                  </h3>
                  <video
                    src={quote.videoUrl}
                    controls
                    className="w-full rounded-lg border border-slate-200 max-h-[400px]"
                  />
                  <p className="text-xs text-slate-600 mt-2">
                    In diesem Video zeigen wir Ihnen, wie Sie die optionalen und alternativen Positionen auswählen können.
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex justify-between items-start border-b pb-6">
              <div>
                <div className="w-32 h-16 mb-4 flex items-center justify-center overflow-hidden bg-transparent">
                  {company?.logo ? (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${company.logo}`}
                      alt="Firmenlogo" 
                      className="w-full h-full object-contain bg-transparent"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center rounded-lg text-slate-400 font-bold text-xl border-2 border-dashed border-slate-300">
                      LOGO
                    </div>
                  )}
                </div>
                <div className="text-sm text-slate-600">
                  <p className="font-semibold">{company?.name}</p>
                  <p>{company?.address}</p>
                  <p>{company?.city}</p>
                  <p>Tel: {company?.phone}</p>
                  <p>E-Mail: {company?.email}</p>
                </div>
              </div>
              <div className="text-right">
                <h1 className="text-3xl font-mono font-bold text-slate-800 mb-2 tracking-widest">ANGEBOT</h1>
                <div className="text-sm text-slate-600 space-y-1">
                  <p><span className="font-medium">Angebots-Nr.:</span> <span className="font-mono">{quote.quoteNumber}</span></p>
                  <p><span className="font-medium">Datum:</span> {new Date(quote.createdAt).toLocaleDateString('de-DE')}</p>
                  <p><span className="font-medium">Gültig bis:</span> {new Date(new Date(quote.createdAt).setDate(new Date(quote.createdAt).getDate() + 30)).toLocaleDateString('de-DE')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Customer Info */}
        {quote.customerData && (
          <Card className="print-card">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-3 text-slate-800">Auftraggeber:</h3>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-semibold">{quote.customerData.company || 'Firmenname'}</p>
                <p>{quote.customerData.name || 'Ansprechpartner'}</p>
                <p>{quote.customerData.address || 'Adresse'}</p>
                <p>{quote.customerData.city || 'PLZ Ort'}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Intro Text */}
        {quote.quoteIntro && (
          <Card className="print-card">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-3 text-slate-800">Einleitung:</h3>
              <RichText value={quote.quoteIntro} className="text-slate-700 leading-relaxed" />
            </CardContent>
          </Card>
        )}

        {/* Weitere Informationen - direkt nach Einleitung */}
        {quote.additionalInfo && (
          <Card className="print-card">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-3 text-slate-800">Weitere Informationen:</h3>
              <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                {quote.additionalInfo}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Warum dieses Angebot für Sie? */}
        {quote.customerArguments && (
          <Card className="print-card" style={{ background: `linear-gradient(135deg, ${companyColors.primary}08, ${companyColors.primary}15)`, borderLeft: `4px solid ${companyColors.primary}` }}>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-3" style={{ color: companyColors.primary }}>
                Warum dieses Angebot für Sie?
              </h3>
              <div className="text-slate-700 leading-relaxed prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: quote.customerArguments }} />
            </CardContent>
          </Card>
        )}

        {/* Einmalige Leistungen */}
        {einmaligServices.length > 0 && (
          <Card className="print-card">
            <CardContent className="pt-6">
              <button
                onClick={() => toggleSection('einmalig')}
                className="flex items-center justify-between w-full no-print"
              >
                <h3 className="text-lg font-semibold mb-4" style={{ color: companyColors.einmalig }}>
                  Einmalige Leistungen
                </h3>
                {isSectionCollapsed('einmalig') ? <ChevronDown className="w-5 h-5 text-slate-400 mb-4" /> : <ChevronUp className="w-5 h-5 text-slate-400 mb-4" />}
              </button>
              {!isSectionCollapsed('einmalig') && (
                <><div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2" style={{ borderColor: `${companyColors.einmalig}33` }}>
                      {einmaligServices.some(s => s.isOptional || (s.alternatives && s.alternatives.length > 0)) && (
                        <th className="text-left py-2 px-2 text-sm font-semibold" style={{ color: companyColors.einmalig }}></th>
                      )}
                      <th className="text-left py-2 px-2 text-sm font-semibold" style={{ color: companyColors.einmalig }}>Pos.</th>
                      <th className="text-left py-2 px-2 text-sm font-semibold" style={{ color: companyColors.einmalig }}>Leistung</th>
                      <th className="text-right py-2 px-2 text-sm font-semibold" style={{ color: companyColors.einmalig }}>Menge</th>
                      <th className="text-right py-2 px-2 text-sm font-semibold" style={{ color: companyColors.einmalig }}>Einzelpreis</th>
                      <th className="text-right py-2 px-2 text-sm font-semibold" style={{ color: companyColors.einmalig }}>Gesamtpreis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {einmaligServices.map((service, index) => {
                      // Check for selected alternative
                      let effectivePrice = service.customPrice !== null && service.customPrice !== undefined ? service.customPrice : service.price;
                      if (service.alternatives && service.alternatives.length > 0) {
                        const selectedAltId = selectedAlternatives[service.id] || service.selectedAlternativeId;
                        if (selectedAltId) {
                          const selectedAlt = service.alternatives.find(a => a.id === selectedAltId);
                          if (selectedAlt) {
                            effectivePrice = selectedAlt.price;
                          }
                        }
                      }
                      const price = effectivePrice;
                      const qty = getQty(service); const total = price * qty;
                      const isSelected = service.isOptional ? optionalServicesSelected[service.id] : true;
                      const hasOptionalOrAlternatives = einmaligServices.some(s => s.isOptional || (s.alternatives && s.alternatives.length > 0) || s.hasAlternative);
                      
                      return (
                        <tr 
                          key={index} 
                          className="border-b hover:bg-slate-50" 
                          style={{ 
                            borderColor: `${companyColors.einmalig}1a`,
                            opacity: service.isOptional && !isSelected ? 0.5 : 1
                          }}
                        >
                          {hasOptionalOrAlternatives && (
                            <td className="py-3 px-2" onClick={(e) => e.stopPropagation()}>
                              {service.isOptional && (
                                <Checkbox
                                  checked={optionalServicesSelected[service.id] || false}
                                  onCheckedChange={() => toggleOptionalService(service.id)}
                                />
                              )}
                            </td>
                          )}
                          <td className="py-3 px-2 text-sm">{index + 1}</td>
                          <td className="py-3 px-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{service.name}</p>
                                {service.isOptional && (
                                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">
                                    Optional
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-600 mt-1">{service.description}</p>
                              {service.customDescription && (
                                <p className="text-xs text-slate-500 italic mt-1">{service.customDescription}</p>
                              )}
                              
                              {/* Alternative Positionen Auswahl (Legacy System) */}
                              {service.alternatives && service.alternatives.length > 0 && (
                                <div className="mt-3 p-2 bg-slate-50 rounded-lg border border-slate-200">
                                  <p className="text-xs font-semibold text-slate-600 mb-2">Wählen Sie eine Option:</p>
                                  <div className="space-y-1">
                                    {service.alternatives.map((alt) => {
                                      const isSelectedAlt = (selectedAlternatives[service.id] || service.selectedAlternativeId) === alt.id;
                                      return (
                                        <button
                                          key={alt.id}
                                          onClick={() => selectAlternative(service.id, alt.id)}
                                          className={`w-full text-left p-2 rounded text-xs transition-colors ${
                                            isSelectedAlt 
                                              ? 'bg-slate-900 text-white'
                                              : 'bg-white hover:bg-slate-50 text-slate-800 border border-slate-200'
                                          }`}
                                        >
                                          <div className="flex justify-between items-center">
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2">
                                                <span className="font-semibold">{alt.title}</span>
                                                {alt.isDefault && !isSelectedAlt && (
                                                  <span className="text-xs text-slate-500 bg-slate-100 px-1 rounded">Standard</span>
                                                )}
                                                {isSelectedAlt && (
                                                  <span className="text-xs bg-green-500 px-1 rounded">✓ Gewählt</span>
                                                )}
                                              </div>
                                              {alt.description && (
                                                <p className={`text-xs mt-0.5 ${isSelectedAlt ? 'text-slate-200' : 'text-slate-600'}`}>
                                                  {alt.description}
                                                </p>
                                              )}
                                            </div>
                                            <div className={`font-bold ${isSelectedAlt ? 'text-white' : 'text-slate-500'}`}>
                                              {alt.price.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                                            </div>
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              
                              {/* Alternative Position (Neues System mit hasAlternative) */}
                              {service.hasAlternative && service.alternativeName && (
                                <div className="mt-3 p-2 bg-slate-50 rounded-lg border border-slate-200">
                                  <p className="text-xs font-semibold text-slate-600 mb-2">Alternative wählen:</p>
                                  <div className="space-y-2">
                                    {/* Hauptposition */}
                                    <button
                                      onClick={() => setSelectedAlternatives(prev => ({ ...prev, [service.id]: 'main' }))}
                                      className={`w-full text-left p-2 rounded text-xs transition-colors ${
                                        (!selectedAlternatives[service.id] || selectedAlternatives[service.id] === 'main')
                                          ? 'bg-slate-900 text-white'
                                          : 'bg-white hover:bg-slate-50 text-slate-800 border border-slate-200'
                                      }`}
                                    >
                                      <div className="flex justify-between items-center">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-semibold">{service.name}</span>
                                            {(!selectedAlternatives[service.id] || selectedAlternatives[service.id] === 'main') && (
                                              <span className="text-xs bg-green-500 px-1 rounded">✓ Gewählt</span>
                                            )}
                                          </div>
                                          {service.description && (
                                            <p className={`text-xs mt-0.5 ${(!selectedAlternatives[service.id] || selectedAlternatives[service.id] === 'main') ? 'text-slate-200' : 'text-slate-600'}`}>
                                              {service.description}
                                            </p>
                                          )}
                                        </div>
                                        <div className={`font-bold ${(!selectedAlternatives[service.id] || selectedAlternatives[service.id] === 'main') ? 'text-white' : 'text-slate-500'}`}>
                                          {service.price.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                                        </div>
                                      </div>
                                    </button>
                                    
                                    {/* Alternative Position */}
                                    <button
                                      onClick={() => setSelectedAlternatives(prev => ({ ...prev, [service.id]: 'alternative' }))}
                                      className={`w-full text-left p-2 rounded text-xs transition-colors ${
                                        selectedAlternatives[service.id] === 'alternative'
                                          ? 'bg-slate-900 text-white'
                                          : 'bg-white hover:bg-slate-50 text-slate-800 border border-slate-200'
                                      }`}
                                    >
                                      <div className="flex justify-between items-center">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-semibold">{service.alternativeName}</span>
                                            {selectedAlternatives[service.id] === 'alternative' && (
                                              <span className="text-xs bg-green-500 px-1 rounded">✓ Gewählt</span>
                                            )}
                                          </div>
                                          {service.alternativeDescription && (
                                            <p className={`text-xs mt-0.5 ${selectedAlternatives[service.id] === 'alternative' ? 'text-slate-200' : 'text-slate-600'}`}>
                                              {service.alternativeDescription}
                                            </p>
                                          )}
                                        </div>
                                        <div className={`font-bold ${selectedAlternatives[service.id] === 'alternative' ? 'text-white' : 'text-slate-500'}`}>
                                          {(service.alternativePrice || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                                        </div>
                                      </div>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-2 text-right text-sm">
                            <div className="flex items-center justify-end gap-1">
                              <button type="button" className="w-5 h-5 rounded bg-slate-100 hover:bg-slate-200 text-xs flex items-center justify-center no-print"
                                onClick={() => setLocalQuantities(prev => ({...prev, [service.id]: Math.max(1, (prev[service.id] !== undefined ? prev[service.id] : service.quantity) - 1)}))}>-</button>
                              <span className="w-6 text-center">{getQty(service)}</span>
                              <button type="button" className="w-5 h-5 rounded bg-slate-100 hover:bg-slate-200 text-xs flex items-center justify-center no-print"
                                onClick={() => setLocalQuantities(prev => ({...prev, [service.id]: (prev[service.id] !== undefined ? prev[service.id] : service.quantity) + 1}))}>+</button>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-right text-sm">
                            {price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                          </td>
                          <td className="py-3 px-2 text-right text-sm font-medium" style={{ color: companyColors.einmalig }}>
                            {total.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Kostenübersicht */}
              <div className="p-6 rounded-lg border mt-6" style={{ 
                backgroundColor: `${companyColors.einmalig}15`, 
                borderColor: `${companyColors.einmalig}50` 
              }}>
                <h4 className="text-md font-semibold mb-4" style={{ color: companyColors.einmalig }}>Kostenübersicht einmalige Leistungen</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Nettobetrag (Pflichtpositionen):</span>
                    <span className="font-medium">€{einmaligTotals.subtotal.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}</span>
                  </div>
                  {einmaligTotals.optionalSubtotal > 0 && (
                    <div className="flex justify-between text-yellow-700">
                      <span>+ Optionale Positionen:</span>
                      <span className="font-medium">€{einmaligTotals.optionalSubtotal.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}</span>
                    </div>
                  )}
                  {einmaligTotals.globalDiscount > 0 && einmaligTotals.discountInfo && (
                    <div className="flex justify-between text-slate-500">
                      <span>- Rabatt ({einmaligTotals.discountInfo.type === 'percentage' ? `${einmaligTotals.discountInfo.value}%` : `${einmaligTotals.discountInfo.value}€`}):</span>
                      <span className="font-medium">-€{einmaligTotals.globalDiscount.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-2 mt-2" style={{ borderColor: `${companyColors.einmalig}50` }}>
                    <span>Netto-Endpreis:</span>
                    <span className="font-medium">€{einmaligTotals.netto.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>MwSt. (19%):</span>
                    <span className="font-medium">€{einmaligTotals.mwst.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}</span>
                  </div>
                  <div className="flex justify-between border-t-2 pt-2 mt-2" style={{ borderColor: companyColors.einmalig }}>
                    <span className="font-bold text-lg">Gesamtbetrag inkl. MwSt.:</span>
                    <span className="font-bold text-lg" style={{ color: companyColors.einmalig }}>€{einmaligTotals.brutto.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}</span>
                  </div>
                </div>
              </div>
              </>
              )}
            </CardContent>
          </Card>
        )}
        {monatlichServices.length > 0 && (
          <Card className="print-card">
            <CardContent className="pt-6">
              <button
                onClick={() => toggleSection('monatlich')}
                className="flex items-center justify-between w-full no-print"
              >
                <h3 className="text-lg font-semibold mb-4" style={{ color: companyColors.monatlich }}>
                  Monatliche Leistungen
                </h3>
                {isSectionCollapsed('monatlich') ? <ChevronDown className="w-5 h-5 text-slate-400 mb-4" /> : <ChevronUp className="w-5 h-5 text-slate-400 mb-4" />}
              </button>
              {!isSectionCollapsed('monatlich') && (
              <><div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2" style={{ borderColor: `${companyColors.monatlich}33` }}>
                      {monatlichServices.some(s => s.isOptional || (s.alternatives && s.alternatives.length > 0)) && (
                        <th className="text-left py-2 px-2 text-sm font-semibold" style={{ color: companyColors.monatlich }}></th>
                      )}
                      <th className="text-left py-2 px-2 text-sm font-semibold" style={{ color: companyColors.monatlich }}>Pos.</th>
                      <th className="text-left py-2 px-2 text-sm font-semibold" style={{ color: companyColors.monatlich }}>Leistung</th>
                      <th className="text-right py-2 px-2 text-sm font-semibold" style={{ color: companyColors.monatlich }}>Menge</th>
                      <th className="text-right py-2 px-2 text-sm font-semibold" style={{ color: companyColors.monatlich }}>Monatspreis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monatlichServices.map((service, index) => {
                      // Check for selected alternative
                      let effectivePrice = service.customPrice !== null && service.customPrice !== undefined ? service.customPrice : service.price;
                      if (service.alternatives && service.alternatives.length > 0) {
                        const selectedAltId = selectedAlternatives[service.id] || service.selectedAlternativeId;
                        if (selectedAltId) {
                          const selectedAlt = service.alternatives.find(a => a.id === selectedAltId);
                          if (selectedAlt) {
                            effectivePrice = selectedAlt.price;
                          }
                        }
                      }
                      const price = effectivePrice;
                      const qty = getQty(service); const total = price * qty;
                      const isSelected = service.isOptional ? optionalServicesSelected[service.id] : true;
                      const hasOptionalOrAlternatives = monatlichServices.some(s => s.isOptional || (s.alternatives && s.alternatives.length > 0) || s.hasAlternative);
                      
                      return (
                        <tr 
                          key={index} 
                          className="border-b hover:bg-slate-50" 
                          style={{ 
                            borderColor: `${companyColors.monatlich}1a`,
                            opacity: service.isOptional && !isSelected ? 0.5 : 1
                          }}
                        >
                          {hasOptionalOrAlternatives && (
                            <td className="py-3 px-2">
                              {service.isOptional && (
                                <Checkbox
                                  checked={optionalServicesSelected[service.id] || false}
                                  onCheckedChange={() => toggleOptionalService(service.id)}
                                />
                              )}
                            </td>
                          )}
                          <td className="py-3 px-2 text-sm">{index + 1}</td>
                          <td className="py-3 px-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{service.name}</p>
                                {service.isOptional && (
                                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">
                                    Optional
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-600 mt-1">{service.description}</p>
                              
                              {/* Alternative Positionen Auswahl (Legacy) */}
                              {service.alternatives && service.alternatives.length > 0 && (
                                <div className="mt-3 p-2 bg-orange-50 rounded-lg border border-orange-200">
                                  <p className="text-xs font-semibold text-orange-800 mb-2">Wählen Sie eine Option:</p>
                                  <div className="space-y-1">
                                    {service.alternatives.map((alt) => {
                                      const isSelectedAlt = (selectedAlternatives[service.id] || service.selectedAlternativeId) === alt.id;
                                      return (
                                        <button
                                          key={alt.id}
                                          onClick={() => selectAlternative(service.id, alt.id)}
                                          className={`w-full text-left p-2 rounded text-xs transition-colors ${
                                            isSelectedAlt 
                                              ? 'bg-orange-600 text-white' 
                                              : 'bg-white hover:bg-orange-100 text-slate-800'
                                          }`}
                                        >
                                          <div className="flex justify-between items-center">
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2">
                                                <span className="font-semibold">{alt.title}</span>
                                                {alt.isDefault && !isSelectedAlt && (
                                                  <span className="text-xs text-orange-600 bg-orange-100 px-1 rounded">Standard</span>
                                                )}
                                                {isSelectedAlt && (
                                                  <span className="text-xs bg-green-500 px-1 rounded">✓ Gewählt</span>
                                                )}
                                              </div>
                                              {alt.description && (
                                                <p className={`text-xs mt-0.5 ${isSelectedAlt ? 'text-orange-100' : 'text-slate-600'}`}>
                                                  {alt.description}
                                                </p>
                                              )}
                                            </div>
                                            <div className={`font-bold ${isSelectedAlt ? 'text-white' : 'text-orange-600'}`}>
                                              {alt.price.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                                            </div>
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              
                              {/* Alternative Position (Neues System mit hasAlternative) */}
                              {service.hasAlternative && service.alternativeName && (
                                <div className="mt-3 p-2 bg-slate-50 rounded-lg border border-slate-200">
                                  <p className="text-xs font-semibold text-slate-600 mb-2">Alternative wählen:</p>
                                  <div className="space-y-2">
                                    {/* Hauptposition */}
                                    <button
                                      onClick={() => setSelectedAlternatives(prev => ({ ...prev, [service.id]: 'main' }))}
                                      className={`w-full text-left p-2 rounded text-xs transition-colors ${
                                        (!selectedAlternatives[service.id] || selectedAlternatives[service.id] === 'main')
                                          ? 'bg-orange-600 text-white' 
                                          : 'bg-white hover:bg-orange-100 text-slate-800'
                                      }`}
                                    >
                                      <div className="flex justify-between items-center">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-semibold">{service.name}</span>
                                            {(!selectedAlternatives[service.id] || selectedAlternatives[service.id] === 'main') && (
                                              <span className="text-xs bg-green-500 px-1 rounded">✓ Gewählt</span>
                                            )}
                                          </div>
                                          {service.description && (
                                            <p className={`text-xs mt-0.5 ${(!selectedAlternatives[service.id] || selectedAlternatives[service.id] === 'main') ? 'text-orange-100' : 'text-slate-600'}`}>
                                              {service.description}
                                            </p>
                                          )}
                                        </div>
                                        <div className={`font-bold ${(!selectedAlternatives[service.id] || selectedAlternatives[service.id] === 'main') ? 'text-white' : 'text-orange-600'}`}>
                                          {service.price.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                                        </div>
                                      </div>
                                    </button>
                                    
                                    {/* Alternative Position */}
                                    <button
                                      onClick={() => setSelectedAlternatives(prev => ({ ...prev, [service.id]: 'alternative' }))}
                                      className={`w-full text-left p-2 rounded text-xs transition-colors ${
                                        selectedAlternatives[service.id] === 'alternative'
                                          ? 'bg-slate-900 text-white'
                                          : 'bg-white hover:bg-slate-50 text-slate-800 border border-slate-200'
                                      }`}
                                    >
                                      <div className="flex justify-between items-center">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-semibold">{service.alternativeName}</span>
                                            {selectedAlternatives[service.id] === 'alternative' && (
                                              <span className="text-xs bg-green-500 px-1 rounded">✓ Gewählt</span>
                                            )}
                                          </div>
                                          {service.alternativeDescription && (
                                            <p className={`text-xs mt-0.5 ${selectedAlternatives[service.id] === 'alternative' ? 'text-slate-200' : 'text-slate-600'}`}>
                                              {service.alternativeDescription}
                                            </p>
                                          )}
                                        </div>
                                        <div className={`font-bold ${selectedAlternatives[service.id] === 'alternative' ? 'text-white' : 'text-slate-500'}`}>
                                          {(service.alternativePrice || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                                        </div>
                                      </div>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-2 text-right text-sm">
                            <div className="flex items-center justify-end gap-1">
                              <button type="button" className="w-5 h-5 rounded bg-slate-100 hover:bg-slate-200 text-xs flex items-center justify-center no-print"
                                onClick={() => setLocalQuantities(prev => ({...prev, [service.id]: Math.max(1, (prev[service.id] !== undefined ? prev[service.id] : service.quantity) - 1)}))}>-</button>
                              <span className="w-6 text-center">{getQty(service)}</span>
                              <button type="button" className="w-5 h-5 rounded bg-slate-100 hover:bg-slate-200 text-xs flex items-center justify-center no-print"
                                onClick={() => setLocalQuantities(prev => ({...prev, [service.id]: (prev[service.id] !== undefined ? prev[service.id] : service.quantity) + 1}))}>+</button>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-right text-sm font-medium" style={{ color: companyColors.monatlich }}>
                            {total.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€/Monat
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Kostenübersicht */}
              <div className="p-6 rounded-lg border mt-6" style={{ 
                backgroundColor: `${companyColors.monatlich}15`, 
                borderColor: `${companyColors.monatlich}50` 
              }}>
                <h4 className="text-md font-semibold mb-4" style={{ color: companyColors.monatlich }}>Kostenübersicht monatliche Leistungen</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Nettobetrag (Pflichtpositionen):</span>
                    <span className="font-medium">€{monatlichTotals.subtotal.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Monat</span>
                  </div>
                  {monatlichTotals.optionalSubtotal > 0 && (
                    <div className="flex justify-between text-yellow-700">
                      <span>+ Optionale Positionen:</span>
                      <span className="font-medium">€{monatlichTotals.optionalSubtotal.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}/Monat</span>
                    </div>
                  )}
                  {monatlichTotals.globalDiscount > 0 && monatlichTotals.discountInfo && (
                    <div className="flex justify-between text-slate-500">
                      <span>- Rabatt ({monatlichTotals.discountInfo.type === 'percentage' ? `${monatlichTotals.discountInfo.value}%` : `${monatlichTotals.discountInfo.value}€`}):</span>
                      <span className="font-medium">-€{monatlichTotals.globalDiscount.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}/Monat</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Netto-Endpreis:</span>
                    <span className="font-medium">€{monatlichTotals.netto.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Monat</span>
                  </div>
                  <div className="flex justify-between">
                    <span>MwSt. (19%):</span>
                    <span className="font-medium">€{monatlichTotals.mwst.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Monat</span>
                  </div>
                  <div className="flex justify-between border-t-2 pt-2 mt-2" style={{ borderColor: companyColors.monatlich }}>
                    <span className="font-bold text-lg">Gesamtbetrag inkl. MwSt.:</span>
                    <span className="font-bold text-lg" style={{ color: companyColors.monatlich }}>€{monatlichTotals.brutto.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Monat</span>
                  </div>
                </div>
              </div>
              </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Jährliche Leistungen */}
        {jaehrlichServices.length > 0 && (
          <Card className="print-card">
            <CardContent className="pt-6">
              <button
                onClick={() => toggleSection('jaehrlich')}
                className="flex items-center justify-between w-full no-print"
              >
                <h3 className="text-lg font-semibold mb-4" style={{ color: companyColors.jaehrlich }}>
                  Jährliche Leistungen
                </h3>
                {isSectionCollapsed('jaehrlich') ? <ChevronDown className="w-5 h-5 text-slate-400 mb-4" /> : <ChevronUp className="w-5 h-5 text-slate-400 mb-4" />}
              </button>
              {!isSectionCollapsed('jaehrlich') && (
              <><div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2" style={{ borderColor: `${companyColors.jaehrlich}33` }}>
                      {jaehrlichServices.some(s => s.isOptional || (s.alternatives && s.alternatives.length > 0)) && (
                        <th className="text-left py-2 px-2 text-sm font-semibold" style={{ color: companyColors.jaehrlich }}></th>
                      )}
                      <th className="text-left py-2 px-2 text-sm font-semibold" style={{ color: companyColors.jaehrlich }}>Pos.</th>
                      <th className="text-left py-2 px-2 text-sm font-semibold" style={{ color: companyColors.jaehrlich }}>Leistung</th>
                      <th className="text-right py-2 px-2 text-sm font-semibold" style={{ color: companyColors.jaehrlich }}>Menge</th>
                      <th className="text-right py-2 px-2 text-sm font-semibold" style={{ color: companyColors.jaehrlich }}>Jahrespreis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jaehrlichServices.map((service, index) => {
                      // Check for selected alternative
                      let effectivePrice = service.customPrice !== null && service.customPrice !== undefined ? service.customPrice : service.price;
                      if (service.alternatives && service.alternatives.length > 0) {
                        const selectedAltId = selectedAlternatives[service.id] || service.selectedAlternativeId;
                        if (selectedAltId) {
                          const selectedAlt = service.alternatives.find(a => a.id === selectedAltId);
                          if (selectedAlt) {
                            effectivePrice = selectedAlt.price;
                          }
                        }
                      }
                      const price = effectivePrice;
                      const qty = getQty(service); const total = price * qty;
                      const isSelected = service.isOptional ? optionalServicesSelected[service.id] : true;
                      const hasOptionalOrAlternatives = jaehrlichServices.some(s => s.isOptional || (s.alternatives && s.alternatives.length > 0) || s.hasAlternative);
                      
                      return (
                        <tr 
                          key={index} 
                          className="border-b hover:bg-slate-50" 
                          style={{ 
                            borderColor: `${companyColors.jaehrlich}1a`,
                            opacity: service.isOptional && !isSelected ? 0.5 : 1
                          }}
                        >
                          {hasOptionalOrAlternatives && (
                            <td className="py-3 px-2">
                              {service.isOptional && (
                                <Checkbox
                                  checked={optionalServicesSelected[service.id] || false}
                                  onCheckedChange={() => toggleOptionalService(service.id)}
                                />
                              )}
                            </td>
                          )}
                          <td className="py-3 px-2 text-sm">{index + 1}</td>
                          <td className="py-3 px-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{service.name}</p>
                                {service.isOptional && (
                                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">
                                    Optional
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-600 mt-1">{service.description}</p>
                              
                              {/* Alternative Positionen Auswahl (Legacy) */}
                              {service.alternatives && service.alternatives.length > 0 && (
                                <div className="mt-3 p-2 bg-slate-50 rounded-lg border border-slate-200">
                                  <p className="text-xs font-semibold text-slate-600 mb-2">Wählen Sie eine Option:</p>
                                  <div className="space-y-1">
                                    {service.alternatives.map((alt) => {
                                      const isSelectedAlt = (selectedAlternatives[service.id] || service.selectedAlternativeId) === alt.id;
                                      return (
                                        <button
                                          key={alt.id}
                                          onClick={() => selectAlternative(service.id, alt.id)}
                                          className={`w-full text-left p-2 rounded text-xs transition-colors ${
                                            isSelectedAlt 
                                              ? 'bg-slate-900 text-white'
                                              : 'bg-white hover:bg-slate-50 text-slate-800 border border-slate-200'
                                          }`}
                                        >
                                          <div className="flex justify-between items-center">
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2">
                                                <span className="font-semibold">{alt.title}</span>
                                                {alt.isDefault && !isSelectedAlt && (
                                                  <span className="text-xs text-slate-500 bg-slate-100 px-1 rounded">Standard</span>
                                                )}
                                                {isSelectedAlt && (
                                                  <span className="text-xs bg-green-500 px-1 rounded">✓ Gewählt</span>
                                                )}
                                              </div>
                                              {alt.description && (
                                                <p className={`text-xs mt-0.5 ${isSelectedAlt ? 'text-slate-200' : 'text-slate-600'}`}>
                                                  {alt.description}
                                                </p>
                                              )}
                                            </div>
                                            <div className={`font-bold ${isSelectedAlt ? 'text-white' : 'text-slate-500'}`}>
                                              {alt.price.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                                            </div>
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              
                              {/* Alternative Position (Neues System mit hasAlternative) */}
                              {service.hasAlternative && service.alternativeName && (
                                <div className="mt-3 p-2 bg-slate-50 rounded-lg border border-slate-200">
                                  <p className="text-xs font-semibold text-slate-600 mb-2">Alternative wählen:</p>
                                  <div className="space-y-2">
                                    {/* Hauptposition */}
                                    <button
                                      onClick={() => setSelectedAlternatives(prev => ({ ...prev, [service.id]: 'main' }))}
                                      className={`w-full text-left p-2 rounded text-xs transition-colors ${
                                        (!selectedAlternatives[service.id] || selectedAlternatives[service.id] === 'main')
                                          ? 'bg-slate-900 text-white'
                                          : 'bg-white hover:bg-slate-50 text-slate-800 border border-slate-200'
                                      }`}
                                    >
                                      <div className="flex justify-between items-center">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-semibold">{service.name}</span>
                                            {(!selectedAlternatives[service.id] || selectedAlternatives[service.id] === 'main') && (
                                              <span className="text-xs bg-green-500 px-1 rounded">✓ Gewählt</span>
                                            )}
                                          </div>
                                          {service.description && (
                                            <p className={`text-xs mt-0.5 ${(!selectedAlternatives[service.id] || selectedAlternatives[service.id] === 'main') ? 'text-slate-200' : 'text-slate-600'}`}>
                                              {service.description}
                                            </p>
                                          )}
                                        </div>
                                        <div className={`font-bold ${(!selectedAlternatives[service.id] || selectedAlternatives[service.id] === 'main') ? 'text-white' : 'text-slate-500'}`}>
                                          {service.price.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                                        </div>
                                      </div>
                                    </button>
                                    
                                    {/* Alternative Position */}
                                    <button
                                      onClick={() => setSelectedAlternatives(prev => ({ ...prev, [service.id]: 'alternative' }))}
                                      className={`w-full text-left p-2 rounded text-xs transition-colors ${
                                        selectedAlternatives[service.id] === 'alternative'
                                          ? 'bg-slate-900 text-white'
                                          : 'bg-white hover:bg-slate-50 text-slate-800 border border-slate-200'
                                      }`}
                                    >
                                      <div className="flex justify-between items-center">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-semibold">{service.alternativeName}</span>
                                            {selectedAlternatives[service.id] === 'alternative' && (
                                              <span className="text-xs bg-green-500 px-1 rounded">✓ Gewählt</span>
                                            )}
                                          </div>
                                          {service.alternativeDescription && (
                                            <p className={`text-xs mt-0.5 ${selectedAlternatives[service.id] === 'alternative' ? 'text-slate-200' : 'text-slate-600'}`}>
                                              {service.alternativeDescription}
                                            </p>
                                          )}
                                        </div>
                                        <div className={`font-bold ${selectedAlternatives[service.id] === 'alternative' ? 'text-white' : 'text-slate-500'}`}>
                                          {(service.alternativePrice || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                                        </div>
                                      </div>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-2 text-right text-sm">
                            <div className="flex items-center justify-end gap-1">
                              <button type="button" className="w-5 h-5 rounded bg-slate-100 hover:bg-slate-200 text-xs flex items-center justify-center no-print"
                                onClick={() => setLocalQuantities(prev => ({...prev, [service.id]: Math.max(1, (prev[service.id] !== undefined ? prev[service.id] : service.quantity) - 1)}))}>-</button>
                              <span className="w-6 text-center">{getQty(service)}</span>
                              <button type="button" className="w-5 h-5 rounded bg-slate-100 hover:bg-slate-200 text-xs flex items-center justify-center no-print"
                                onClick={() => setLocalQuantities(prev => ({...prev, [service.id]: (prev[service.id] !== undefined ? prev[service.id] : service.quantity) + 1}))}>+</button>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-right text-sm font-medium" style={{ color: companyColors.jaehrlich }}>
                            {total.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€/Jahr
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Kostenübersicht */}
              <div className="p-6 rounded-lg border mt-6" style={{ 
                backgroundColor: `${companyColors.jaehrlich}15`, 
                borderColor: `${companyColors.jaehrlich}50` 
              }}>
                <h4 className="text-md font-semibold mb-4" style={{ color: companyColors.jaehrlich }}>Kostenübersicht jährliche Leistungen</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Nettobetrag (Pflichtpositionen):</span>
                    <span className="font-medium">€{jaehrlichTotals.subtotal.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Jahr</span>
                  </div>
                  {jaehrlichTotals.optionalSubtotal > 0 && (
                    <div className="flex justify-between text-yellow-700">
                      <span>+ Optionale Positionen:</span>
                      <span className="font-medium">€{jaehrlichTotals.optionalSubtotal.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}/Jahr</span>
                    </div>
                  )}
                  {jaehrlichTotals.globalDiscount > 0 && jaehrlichTotals.discountInfo && (
                    <div className="flex justify-between text-slate-500">
                      <span>- Rabatt ({jaehrlichTotals.discountInfo.type === 'percentage' ? `${jaehrlichTotals.discountInfo.value}%` : `${jaehrlichTotals.discountInfo.value}€`}):</span>
                      <span className="font-medium">-€{jaehrlichTotals.globalDiscount.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}/Jahr</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Netto-Endpreis:</span>
                    <span className="font-medium">€{jaehrlichTotals.netto.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Jahr</span>
                  </div>
                  <div className="flex justify-between">
                    <span>MwSt. (19%):</span>
                    <span className="font-medium">€{jaehrlichTotals.mwst.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Jahr</span>
                  </div>
                  <div className="flex justify-between border-t-2 pt-2 mt-2" style={{ borderColor: companyColors.jaehrlich }}>
                    <span className="font-bold text-lg">Gesamtbetrag inkl. MwSt.:</span>
                    <span className="font-bold text-lg" style={{ color: companyColors.jaehrlich }}>€{jaehrlichTotals.brutto.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Jahr</span>
                  </div>
                </div>
              </div>
              </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Detaillierte Beschreibungen - Immer anzeigen wenn vorhanden */}
        {(einmaligServices.some(s => s.detailedDescription && (!s.isOptional || optionalServicesSelected[s.id])) || 
          monatlichServices.some(s => s.detailedDescription && (!s.isOptional || optionalServicesSelected[s.id])) || 
          jaehrlichServices.some(s => s.detailedDescription && (!s.isOptional || optionalServicesSelected[s.id]))) && (
          <Card className="print-card">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-6 text-slate-800">Detaillierte Leistungsbeschreibungen:</h3>
              
              {/* Einmalige Leistungen */}
              {einmaligServices.filter(s => s.detailedDescription && (!s.isOptional || optionalServicesSelected[s.id])).length > 0 && (
                <div className="mb-8">
                  <h4 className="text-md font-semibold mb-4" style={{ color: companyColors.einmalig }}>Einmalige Kosten:</h4>
                  <div className="space-y-6">
                    {einmaligServices.filter(s => s.detailedDescription && (!s.isOptional || optionalServicesSelected[s.id])).map((service, index) => (
                      <div key={service.id} className="p-4 rounded-lg border" style={{ 
                        backgroundColor: `${companyColors.einmalig}15`, 
                        borderColor: `${companyColors.einmalig}50` 
                      }}>
                        <h5 className="font-semibold text-slate-800 mb-2">{index + 1}. {service.name}</h5>
                        <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                          {service.detailedDescription}
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Monatliche Leistungen */}
              {monatlichServices.filter(s => s.detailedDescription && (!s.isOptional || optionalServicesSelected[s.id])).length > 0 && (
                <div className="mb-8">
                  <h4 className="text-md font-semibold mb-4" style={{ color: companyColors.monatlich }}>Monatliche Kosten:</h4>
                  <div className="space-y-6">
                    {monatlichServices.filter(s => s.detailedDescription && (!s.isOptional || optionalServicesSelected[s.id])).map((service, index) => (
                      <div key={service.id} className="p-4 rounded-lg border" style={{ 
                        backgroundColor: `${companyColors.monatlich}15`, 
                        borderColor: `${companyColors.monatlich}50` 
                      }}>
                        <h5 className="font-semibold text-slate-800 mb-2">{index + 1}. {service.name}</h5>
                        <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                          {service.detailedDescription}
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Jährliche Leistungen */}
              {jaehrlichServices.filter(s => s.detailedDescription && (!s.isOptional || optionalServicesSelected[s.id])).length > 0 && (
                <div>
                  <h4 className="text-md font-semibold mb-4" style={{ color: companyColors.jaehrlich }}>Jährliche Kosten:</h4>
                  <div className="space-y-6">
                    {jaehrlichServices.filter(s => s.detailedDescription && (!s.isOptional || optionalServicesSelected[s.id])).map((service, index) => (
                      <div key={service.id} className="p-4 rounded-lg border" style={{ 
                        backgroundColor: `${companyColors.jaehrlich}15`, 
                        borderColor: `${companyColors.jaehrlich}50` 
                      }}>
                        <h5 className="font-semibold text-slate-800 mb-2">{index + 1}. {service.name}</h5>
                        <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                          {service.detailedDescription}
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Angebotsbereiche / Vorlagen */}
        {quote.quoteSections && quote.quoteSections.length > 0 && (
          <Card className="print-card">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-6 text-slate-800">Angebotsbereiche:</h3>
              <div className="space-y-6">
                {quote.quoteSections.map((section, index) => (
                  <div key={index} className="p-4 rounded-lg border border-slate-200 bg-slate-50">
                    <h4 className="font-semibold text-slate-800 mb-3">{section.title}</h4>
                    <div 
                      className="text-sm text-slate-700 leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: section.content
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\*(.*?)\*/g, '<em>$1</em>')
                          .replace(/• /g, '<span style="color: #2563eb;">•</span> ')
                          .replace(/✓ /g, '<span style="color: #16a34a; font-weight: bold;">✓</span> ')
                          .replace(/(\d+)\. /g, '<strong style="color: #7c3aed;">$1.</strong> ')
                          .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #2563eb; text-decoration: underline;">$1</a>')
                          .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width: 200px; height: auto; margin: 10px 0;" />')
                          .replace(/\n/g, '<br />')
                      }}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Zahlungsbedingungen & Konditionen */}
        <Card className="print-card">
          <CardContent className="pt-6">
            <div className="space-y-4 text-sm text-slate-600">
              {/* Zahlungsbedingungen */}
              {quote.paymentTerms && (
                <div>
                  <h4 className="font-semibold text-slate-800 mb-2">Zahlungsbedingungen:</h4>
                  <p>{(() => {
                    switch(quote.paymentTerms) {
                      case '30-tage-netto': return '30 Tage netto'
                      case '14-tage-netto': return '14 Tage netto'
                      case 'sofort-faellig': return 'Sofort fällig'
                      case 'bei-lieferung': return 'Bei Lieferung'
                      case '50-30-20': return '50 % bei Beauftragung, 30 % bei Zwischenabnahme, 20 % bei Fertigstellung'
                      case '50-50': return '50 % des einmaligen Aufwands bei Beauftragung, 50 % bei Fertigstellung'
                      case 'custom': return quote.customPaymentTerms || 'Individuelle Zahlungsbedingungen'
                      default: 
                        // Handle saved-X pattern - show customPaymentTerms if available, otherwise default
                        if (quote.paymentTerms?.startsWith('saved-')) {
                          return quote.customPaymentTerms || '30 Tage netto'
                        }
                        return quote.paymentTerms || '30 Tage netto'
                    }
                  })()}</p>
                </div>
              )}
              
              {/* Lieferzeit */}
              {quote.deliveryTime && (
                <div>
                  <h4 className="font-semibold text-slate-800 mb-2">Lieferzeit:</h4>
                  <p>{quote.deliveryTime}</p>
                </div>
              )}
              
              {/* Gültigkeit */}
              {quote.validity && (
                <div>
                  <h4 className="font-semibold text-slate-800 mb-2">Gültigkeit:</h4>
                  <p>{quote.validity}</p>
                </div>
              )}
              
              {/* Gewährleistung */}
              {quote.warranty && (
                <div>
                  <h4 className="font-semibold text-slate-800 mb-2">Gewährleistung:</h4>
                  <p>{quote.warranty}</p>
                </div>
              )}
              
              {/* AGBs aus Firmeneinstellungen - als Dropdown */}
              {company?.termsAndConditions && (
                <div className="pt-4 border-t">
                  <details className="group">
                    <summary className="font-semibold text-slate-800 cursor-pointer flex items-center justify-between hover:text-slate-900 transition-colors">
                      <span>Allgemeine Geschäftsbedingungen</span>
                      <svg className="w-5 h-5 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </summary>
                    <div className="text-xs text-slate-700 whitespace-pre-wrap mt-3 pt-3 border-t border-slate-200">
                      {company.termsAndConditions.split('\n').map((line, idx) => {
                        // Format bold text with **text**
                        const formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                        return (
                          <p key={idx} dangerouslySetInnerHTML={{ __html: formattedLine }} className="mb-1" />
                        );
                      })}
                    </div>
                  </details>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Was kommt als Nächstes */}
        {quote.nextSteps && (
          <Card className="print-card" style={{ borderLeft: `4px solid ${companyColors.primary}` }}>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-3" style={{ color: companyColors.primary }}>
                Was kommt als Nächstes?
              </h3>
              <div className="text-slate-700 leading-relaxed prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: quote.nextSteps }} />
            </CardContent>
          </Card>
        )}

        {/* Feedback-Tool */}
        <Card className="print-card no-print">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 text-slate-800">Ihr Feedback zum Angebot</h3>
            {feedbackSubmitted ? (
              <div className="text-center py-4">
                <Check className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-emerald-700 font-medium">Vielen Dank für Ihr Feedback!</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex gap-3 justify-center">
                  {[
                    { rating: 'positive', icon: ThumbsUp, label: 'Passt gut', color: 'green' },
                    { rating: 'neutral', icon: Minus, label: 'Fragen offen', color: 'yellow' },
                    { rating: 'negative', icon: ThumbsDown, label: 'Nicht passend', color: 'red' },
                  ].map(({ rating, icon: Icon, label, color }) => (
                    <button
                      key={rating}
                      onClick={() => handleFeedback(rating)}
                      className={`flex flex-col items-center gap-1 px-5 py-3 rounded-xl border-2 transition-all ${
                        feedbackRating === rating
                          ? `border-${color}-500 bg-${color}-50 text-${color}-700`
                          : 'border-slate-200 hover:border-slate-400 text-slate-600'
                      }`}
                      data-testid={`feedback-${rating}`}
                    >
                      <Icon className="w-6 h-6" />
                      <span className="text-xs font-medium">{label}</span>
                    </button>
                  ))}
                </div>
                {feedbackRating && feedbackRating !== 'positive' && (
                  <Textarea
                    value={feedbackComment}
                    onChange={(e) => setFeedbackComment(e.target.value)}
                    placeholder="Was fehlt oder was können wir verbessern?"
                    rows={3}
                    className="w-full"
                  />
                )}
                {feedbackRating && (
                  <Button onClick={submitFeedback} disabled={submittingFeedback}
                    className="w-full" style={{ backgroundColor: companyColors.primary }}>
                    {submittingFeedback ? 'Wird gesendet...' : 'Feedback absenden'}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Kundenfragen */}
        <Card className="print-card no-print">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 text-slate-800 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" style={{ color: companyColors.primary }} />
              Haben Sie eine Frage?
            </h3>
            {questionSubmitted ? (
              <div className="text-center py-4">
                <Check className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-emerald-700 font-medium">Ihre Frage wurde übermittelt! Wir melden uns bald.</p>
              </div>
            ) : (
              <form onSubmit={submitQuestion} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input value={questionForm.name} onChange={e => setQuestionForm({...questionForm, name: e.target.value})}
                    placeholder="Ihr Name *" required data-testid="question-name" />
                  <Input type="email" value={questionForm.email} onChange={e => setQuestionForm({...questionForm, email: e.target.value})}
                    placeholder="Ihre E-Mail (optional)" data-testid="question-email" />
                </div>
                <Textarea value={questionForm.question} onChange={e => setQuestionForm({...questionForm, question: e.target.value})}
                  placeholder="Ihre Frage zum Angebot... *" rows={3} required data-testid="question-text" />
                <Button type="submit" disabled={submittingQuestion} style={{ backgroundColor: companyColors.primary }}
                  data-testid="question-submit">
                  <Send className="w-4 h-4 mr-2" />
                  {submittingQuestion ? 'Wird gesendet...' : 'Frage senden'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Acceptance Section */}
        {quoteAccepted ? (
          <Card className="print-card bg-emerald-50 border-emerald-100">
            <CardContent className="pt-6">
              <div className="text-center">
                <Check className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-emerald-800 mb-2">
                  Angebot wurde angenommen ✅
                </h3>
                <p className="text-slate-700">
                  Dieses Angebot wurde am {new Date(quote.acceptedAt).toLocaleString('de-DE')} angenommen.
                </p>
                {quote.acceptance && (
                  <div className="mt-4 text-left max-w-md mx-auto text-sm text-slate-600 space-y-1 bg-white rounded-lg p-4 border">
                    <p><strong>Unterzeichner:</strong> {quote.acceptance.signerName}</p>
                    {quote.acceptance.signerCompany && <p><strong>Firma:</strong> {quote.acceptance.signerCompany}</p>}
                    <p><strong>Ort:</strong> {quote.acceptance.signerLocation}</p>
                    <p><strong>Datum:</strong> {quote.acceptance.signatureDate || new Date(quote.acceptedAt).toLocaleDateString('de-DE')}</p>
                    {quote.acceptance.securityToken && <p className="text-xs text-slate-400 break-all"><strong>Sicherheitstoken:</strong> {quote.acceptance.securityToken}</p>}
                    {quote.acceptance.digitalSignature && (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 mb-1">Digitale Unterschrift:</p>
                        <img src={quote.acceptance.digitalSignature} alt="Unterschrift" className="max-h-16 border rounded" />
                      </div>
                    )}
                  </div>
                )}
                <Button onClick={handleSaveAsPdf} className="mt-5"
                  style={{ backgroundColor: companyColors.primary }}
                  data-testid="pdf-download-after-acceptance">
                  Rechtsverbindliches PDF herunterladen
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {!showAcceptanceForm ? (
              <Card className="print-card">
                <CardContent className="pt-6 text-center">
                  <h3 className="text-xl font-semibold text-slate-800 mb-4">
                    Möchten Sie dieses Angebot annehmen?
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Nehmen Sie das Angebot digital an — mit oder ohne Unterschrift.
                  </p>
                  <Button
                    onClick={() => setShowAcceptanceForm(true)}
                    size="lg"
                    className="text-lg px-8"
                    style={{ backgroundColor: companyColors.primary }}
                    data-testid="show-acceptance-form-btn"
                  >
                    <Check className="w-5 h-5 mr-2" />
                    Angebot jetzt annehmen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <QuoteAcceptanceForm
                viewHash={viewHash}
                optionalServicesSelected={optionalServicesSelected}
                companyColors={companyColors}
                onDownloadPdf={handleSaveAsPdf}
                onAcceptanceComplete={(result) => {
                  setQuoteAccepted(true);
                  setShowAcceptanceForm(false);
                  quotesApi.getByViewHash(viewHash).then(response => {
                    setQuoteData(response.data);
                  });
                }}
              />
            )}
          </>
        )}

        {/* Footer */}
        <Card className="print-card">
          <CardContent className="py-6">
            <div className="text-center text-xs text-slate-500 border-t pt-4">
              <p className="font-medium">{company?.name}</p>
              <p>{company?.address} • {company?.city}</p>
              <p>Tel: {company?.phone} • E-Mail: {company?.email}{company?.website ? ` • Web: ${company.website}` : ''}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
};

export default PublicQuoteView;
